import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Printer, Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DEVICE_ID_KEY = "qrpts-device-id";
function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = "HAKIS-QR-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export default function Terminal() {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const canvasWrapRef = useRef(null);

  const deviceId = getOrCreateDeviceId();
  const pinUrl = `${window.location.origin}/pin-entry?device=${encodeURIComponent(deviceId)}`;

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `QRPTS-${deviceId}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const printPoster = () => window.print();

  return (
    <div className="fixed inset-0 z-50 bg-[#050508] text-white overflow-auto" data-testid="terminal-page">
      {/* Top bar — hidden in print */}
      <div className="no-print absolute top-0 left-0 right-0 z-20 p-5 flex items-center justify-between">
        <button
          onClick={() => nav(-1)}
          className="text-white/70 hover:text-white flex items-center gap-2 text-sm"
          data-testid="terminal-back"
        >
          <ArrowLeft className="w-4 h-4" /> Geri
        </button>
        <div className="text-right">
          <div className="font-mono text-xl md:text-2xl font-bold tabular-nums">
            {now.toLocaleTimeString("tr-TR")}
          </div>
          <div className="text-xs text-white/50">
            {now.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </div>
      </div>

      {/* Print-only A4 poster */}
      <div className="only-print print-poster">
        <div className="print-inner">
          <img src="/logo/qrpts-logo.png" alt="QRPTS" className="print-logo" />
          <div className="print-title">Personel Giriş-Çıkış</div>
          <div className="print-sub">Aşağıdaki QR kodu telefonunuzla okutup PIN kodunuzu giriniz.</div>
          <div className="print-qr">
            <QRCodeCanvas value={pinUrl} size={520} level="M" includeMargin={true} />
          </div>
          <ol className="print-steps">
            <li>Telefonunuzun kamerasını açın.</li>
            <li>Yukarıdaki QR kodu okutun.</li>
            <li>Açılan sayfada <b>6 haneli PIN</b> kodunuzu girin.</li>
            <li>Giriş veya çıkış otomatik olarak oluşturulur.</li>
          </ol>
          <div className="print-footer">
            <div>Cihaz: <b>{deviceId}</b></div>
            <div>QRPTS · qrpts.hakisoyuncak.xyz</div>
          </div>
        </div>
      </div>

      {/* On-screen poster preview (hidden in print) */}
      <div className="no-print absolute inset-0 flex items-center justify-center p-4 pt-20 pb-8">
        <div className="w-full max-w-lg">
          <div className="text-center mb-5">
            <img src="/logo/qrpts-logo.png" alt="QRPTS" className="w-16 h-16 object-contain mx-auto mb-3 drop-shadow-2xl" />
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">QR Terminal</h1>
            <p className="text-white/60 text-xs mt-1">
              Cihaz ID: <span className="font-mono text-white">{deviceId}</span>
            </p>
          </div>

          <div
            className="rounded-3xl p-6 border border-white/10 text-center"
            style={{ background: "rgba(20,20,26,0.85)", backdropFilter: "blur(24px)" }}
          >
            <div ref={canvasWrapRef} className="bg-white p-4 rounded-2xl inline-block mb-4">
              <QRCodeCanvas value={pinUrl} size={280} level="M" includeMargin={false} />
            </div>
            <h2 className="font-display text-xl font-bold tracking-tight mb-1">Telefonla Okutun</h2>
            <p className="text-white/60 text-sm mb-4 leading-relaxed">
              QR kodu telefonunuzun kamerasıyla okuttuktan sonra
              <br />
              6 haneli PIN kodunuzu giriniz.
            </p>
            <div className="text-[10px] text-white/40 font-mono break-all border-t border-white/10 pt-3 mb-4">
              {pinUrl}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={download}
                data-testid="qr-download"
                className="h-11 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-transform duration-100 flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Download className="w-4 h-4" /> PNG İndir
              </button>
              <button
                onClick={printPoster}
                data-testid="qr-print"
                className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-95 transition-transform duration-100 flex items-center justify-center gap-2 text-sm font-semibold"
              >
                <Printer className="w-4 h-4" /> Yazdır (A4)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
